import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Component, Input, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { AnswerType, type IngestionFieldDefinition } from "../data.ingestion.service";
import {
    INGESTION_INFO_TEXT_MAX_LENGTH,
    formatCharCountRemaining,
    isOverCharLimit,
} from "../ingestion.field.length.util";
import type { IngestionAdminFormComponent } from "./ingestion.admin.form.component";
import type { IngestionAdminFormFieldsTabComponent } from "./ingestion.admin.form.fields.tab.component";
import { S25FilestoreImageComponent } from "../../../standalone/s25.filestore.image.component";

@TypeManagerDecorator("s25-ng-ingestion-admin-form-field-card")
@Component({
    selector: "s25-ng-ingestion-admin-form-field-card",
    template: `
        <div
            class="field-card"
            [class.field-card--section]="fieldDef.fieldType === 'SECTION'"
            s25-ng-dnd-sortable-item
            [index]="idx"
            [belongsTo]="editor.fieldDefinitions"
            [draggable]="false"
            [attr.data-field-idx]="idx"
        >
            <!-- Field Header -->
            <div class="field-header">
                @if (!editor.isSystemField(fieldDef)) {
                    <span class="field-card__drag-handle" draggable="true" (click)="$event.stopPropagation()">
                        <s25-ng-icon [type]="'dragHandle'" [ariaLabel]="'Move'" />
                    </span>
                }
                <div
                    class="field-header-main"
                    (click)="fieldsTab.toggleFieldConfig(fieldDef)"
                    (keydown.enter)="fieldsTab.toggleFieldConfig(fieldDef)"
                    tabindex="0"
                    role="button"
                    [attr.aria-expanded]="fieldsTab.isFieldExpanded(fieldDef)"
                >
                    <div class="field-title">
                        <s25-ng-icon
                            [type]="'caretDown'"
                            class="collapse-icon"
                            [style.transform]="fieldsTab.isFieldExpanded(fieldDef) ? 'rotate(180deg)' : 'rotate(0deg)'"
                            [ariaLabel]="false"
                        ></s25-ng-icon>
                        <h4>{{ fieldDef.fieldLabel || "Untitled Field" }}</h4>
                        <div class="field-badges">
                            @if (editor.isSystemField(fieldDef)) {
                                <span class="badge badge-warning">System Field</span>
                            }
                            @if (fieldDef.hidden) {
                                <span class="badge badge-secondary">Hidden</span>
                            }
                            @if (fieldDef.required) {
                                <span class="badge badge-primary">Required</span>
                            }
                        </div>
                    </div>
                </div>
                <div class="action-controls">
                    @if (!editor.isSystemField(fieldDef)) {
                        <button
                            type="button"
                            class="aw-button aw-button--outline"
                            (click)="fieldsTab.onAddRuleForField(fieldDef, 'showField', $event)"
                            title="Add a rule to show this field when conditions are met"
                        >
                            Add Rule
                        </button>
                    }
                    <button
                        class="aw-button aw-button--danger--outline"
                        (click)="editor.removeFieldDefinition(fieldDef); $event.stopPropagation()"
                        [disabled]="editor.isSystemField(fieldDef)"
                        title="Remove field"
                    >
                        Remove
                    </button>
                </div>
            </div>

            <!-- Field Configuration -->
            <div class="field-config" [hidden]="!fieldsTab.isFieldExpanded(fieldDef)">
                <!-- Basic Settings -->
                <details class="config-group-collapsible" open>
                    <summary class="config-group-summary">Basic Settings</summary>
                    <div class="config-group-body">
                        <div class="form-row">
                            <div class="form-group">
                                <label>Field Label</label>
                                <input
                                    class="form-control"
                                    type="text"
                                    [(ngModel)]="fieldDef.fieldLabel"
                                    (ngModelChange)="editor.onFieldLabelChange(fieldDef)"
                                    name="fieldLabel-{{ idx }}"
                                    placeholder="Enter field label"
                                    [disabled]="editor.isSystemField(fieldDef)"
                                />
                            </div>
                            <div class="form-group">
                                <label>Field Type</label>
                                <div class="field-type-readonly">
                                    {{ editor.getFieldTypeDisplayLabel(fieldDef.fieldType) }}
                                </div>
                            </div>
                            @if (fieldDef.fieldType !== "SECTION") {
                                <div class="form-group">
                                    <label>Answer Type</label>
                                    <select
                                        class="form-control"
                                        [(ngModel)]="fieldDef.answerType"
                                        (ngModelChange)="editor.onAnswerTypeChange(fieldDef)"
                                        [disabled]="
                                            editor.isSystemField(fieldDef) ||
                                            editor.isJsonField(fieldDef) ||
                                            fieldDef.fieldType === 'FILE' ||
                                            fieldDef.fieldType === 'IMAGE' ||
                                            fieldDef.fieldType === 'EVENT_TYPE' ||
                                            fieldDef.fieldType === 'ORGANIZATION' ||
                                            fieldDef.fieldType === 'CONTACT' ||
                                            fieldDef.fieldType === 'ROLE' ||
                                            fieldDef.fieldType === 'ORG_TYPE' ||
                                            fieldDef.fieldType === 'ORG_RATING' ||
                                            fieldDef.fieldType === 'SECURITY_GROUP' ||
                                            fieldDef.fieldType === 'FORM' ||
                                            fieldDef.fieldType === 'EVENT' ||
                                            fieldDef.fieldType === 'CATEGORIES' ||
                                            fieldDef.fieldType === 'CUSTOM_ATTRIBUTE' ||
                                            fieldDef.fieldType === 'ORG_CUSTOM_ATTRIBUTE' ||
                                            fieldDef.fieldType === 'STARS_RATING' ||
                                            fieldDef.fieldType === 'NUMERIC_SCALE' ||
                                            fieldDef.fieldType === 'REQUIREMENTS' ||
                                            fieldDef.fieldType === 'CONTACT_ROLE' ||
                                            fieldDef.fieldType === 'ORG_CONTACT_ROLE' ||
                                            fieldDef.fieldType === 'ORG_CATEGORIES' ||
                                            fieldDef.fieldType === 'DURATION' ||
                                            fieldDef.fieldType === 'TOGGLE' ||
                                            fieldDef.fieldType === 'ORGANIZATION_MULTISELECT' ||
                                            fieldDef.fieldType === 'RICH_TEXT' ||
                                            fieldDef.fieldType === 'ELECTION_POSITION' ||
                                            fieldDef.fieldType === 'LIVE_25_EVENT_FORM'
                                        "
                                    >
                                        @if (fieldDef.fieldType === "FILE") {
                                            <option value="FILE">File</option>
                                        } @else if (editor.isJsonField(fieldDef)) {
                                            <option value="JSON">JSON</option>
                                        } @else if (fieldDef.fieldType === "RICH_TEXT") {
                                            <option value="LARGE_STRING">Large String (>= 256 chars)</option>
                                        } @else if (fieldDef.fieldType === "IMAGE") {
                                            <option value="LARGE_STRING">Large String (>= 256 chars)</option>
                                        } @else {
                                            <option value="STRING">String (< 256 chars)</option>
                                            <option value="LARGE_STRING">Large String (>= 256 chars)</option>
                                            <option value="INTEGER">Integer</option>
                                            <option value="FLOAT">Decimal</option>
                                            <option value="BOOLEAN">Boolean</option>
                                            <option value="DATETIME">Date/Time</option>
                                        }
                                    </select>
                                </div>
                            }
                            @if (fieldDef.fieldType === "CUSTOM_ATTRIBUTE") {
                                <div class="form-group">
                                    <label>Custom Attribute</label>
                                    <s25-custom-attr-dropdown
                                        [itemTypeId]="1"
                                        [value]="fieldDef.custAtrbId"
                                        (valueChange)="editor.onEventCustomAttributePicked(fieldDef, $event)"
                                    />
                                    <small class="form-text">Event custom attributes with search.</small>
                                </div>
                            }
                            @if (fieldDef.fieldType === "ORG_CUSTOM_ATTRIBUTE") {
                                <div class="form-group">
                                    <label>Organization Custom Attribute</label>
                                    <select
                                        class="form-control"
                                        [ngModel]="fieldDef.custAtrbId"
                                        (ngModelChange)="editor.onOrgCustomAttributePicked(fieldDef, $event)"
                                    >
                                        <option [ngValue]="undefined">Select a custom attribute...</option>
                                        @for (attr of editor.orgCustomAttributes(); track attr.itemId) {
                                            <option [ngValue]="attr.itemId">
                                                {{ attr.itemName }} ({{ attr.itemTypeId }})
                                            </option>
                                        }
                                    </select>
                                    <small class="form-text">Organization custom attributes.</small>
                                </div>
                            }
                            @if (fieldDef.fieldType === "TOGGLE") {
                                <div class="form-group">
                                    <label>"Yes" Label</label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        [ngModel]="fieldDef.options?.[0]?.option ?? 'Yes'"
                                        (ngModelChange)="editor.onToggleOptionLabelChange(fieldDef, 0, $event)"
                                        name="toggleTrueLabel-{{ idx }}"
                                        placeholder="Yes"
                                    />
                                </div>
                                <div class="form-group">
                                    <label>"No" Label</label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        [ngModel]="fieldDef.options?.[1]?.option ?? 'No'"
                                        (ngModelChange)="editor.onToggleOptionLabelChange(fieldDef, 1, $event)"
                                        name="toggleFalseLabel-{{ idx }}"
                                        placeholder="No"
                                    />
                                </div>
                            }
                        </div>
                    </div>
                    <!-- /config-group-body -->
                </details>

                <!-- Field Options -->
                <details class="config-group-collapsible" open>
                    <summary class="config-group-summary">Field Options</summary>
                    <div class="config-group-body">
                        <div class="form-row">
                            <div class="form-group">
                                <label>Info Text</label>
                                <s25-ng-rich-text-editor
                                    [(modelValue)]="fieldDef.infoText"
                                    [disabled]="editor.isSystemField(fieldDef)"
                                ></s25-ng-rich-text-editor>
                                @if (!editor.isSystemField(fieldDef)) {
                                    <small
                                        class="form-text"
                                        [class.char-count--over]="isInfoTextOverLimit(fieldDef.infoText)"
                                    >
                                        {{ formatInfoTextRemainingMessage(fieldDef.infoText) }}
                                    </small>
                                }
                                <s25-ng-checkbox
                                    class="c-margin-top--single"
                                    [(value)]="fieldDef.infoTextExpanded"
                                    [disabled]="editor.isSystemField(fieldDef)"
                                >
                                    Expand info text by default
                                </s25-ng-checkbox>
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label>Default Value</label>
                                @if (
                                    fieldDef.fieldType === "ORGANIZATION" ||
                                    fieldDef.fieldType === "CONTACT" ||
                                    fieldDef.fieldType === "ROLE" ||
                                    fieldDef.fieldType === "ORG_TYPE" ||
                                    fieldDef.fieldType === "ORG_RATING" ||
                                    fieldDef.fieldType === "SECURITY_GROUP" ||
                                    fieldDef.fieldType === "EVENT_TYPE"
                                ) {
                                    <!-- Dropdown selector for special field types -->
                                    @if (fieldDef.fieldType === "ORGANIZATION") {
                                        <s25-ng-dropdown-search-criteria
                                            [type]="'organizations'"
                                            [value]="editor.getDefaultValueDropdownItem(fieldDef)"
                                            (valueChange)="editor.onDefaultValueChange(fieldDef, $event)"
                                            [placeholder]="'Select Organization'"
                                            [readOnly]="editor.isSystemField(fieldDef)"
                                        />
                                    } @else if (fieldDef.fieldType === "CONTACT") {
                                        <s25-ng-dropdown-search-criteria
                                            [type]="'contacts'"
                                            [value]="editor.getDefaultValueDropdownItem(fieldDef)"
                                            (valueChange)="editor.onDefaultValueChange(fieldDef, $event)"
                                            [placeholder]="'Select Contact'"
                                            [readOnly]="editor.isSystemField(fieldDef)"
                                        />
                                    } @else if (fieldDef.fieldType === "ROLE") {
                                        <s25-ng-dropdown-search-criteria
                                            [type]="'organizationRoles'"
                                            [value]="editor.getDefaultValueDropdownItem(fieldDef)"
                                            (valueChange)="editor.onDefaultValueChange(fieldDef, $event)"
                                            [placeholder]="'Select Role'"
                                            [readOnly]="editor.isSystemField(fieldDef)"
                                        />
                                    } @else if (fieldDef.fieldType === "ORG_TYPE") {
                                        <s25-ng-dropdown-search-criteria
                                            [type]="'organizationTypes'"
                                            [value]="editor.getDefaultValueDropdownItem(fieldDef)"
                                            (valueChange)="editor.onDefaultValueChange(fieldDef, $event)"
                                            [placeholder]="'Select Organization Type'"
                                            [readOnly]="editor.isSystemField(fieldDef)"
                                        />
                                    } @else if (fieldDef.fieldType === "ORG_RATING") {
                                        <s25-ng-dropdown-search-criteria
                                            [type]="'organizationRatings'"
                                            [value]="editor.getDefaultValueDropdownItem(fieldDef)"
                                            (valueChange)="editor.onDefaultValueChange(fieldDef, $event)"
                                            [placeholder]="'Select Organization Rating'"
                                            [readOnly]="editor.isSystemField(fieldDef)"
                                        />
                                    } @else if (fieldDef.fieldType === "SECURITY_GROUP") {
                                        <s25-ng-dropdown-search-criteria
                                            [type]="'securityGroups'"
                                            [value]="editor.getDefaultValueDropdownItem(fieldDef)"
                                            (valueChange)="editor.onDefaultValueChange(fieldDef, $event)"
                                            [placeholder]="'Select Security Group'"
                                            [readOnly]="editor.isSystemField(fieldDef)"
                                        />
                                    } @else if (fieldDef.fieldType === "EVENT_TYPE") {
                                        <s25-ng-dropdown-search-criteria
                                            [type]="'eventTypes'"
                                            [value]="editor.getDefaultValueDropdownItem(fieldDef)"
                                            (valueChange)="editor.onDefaultValueChange(fieldDef, $event)"
                                            [placeholder]="'Select Event Type'"
                                            [readOnly]="editor.isSystemField(fieldDef)"
                                        />
                                    }
                                    @if (editor.isValidDefaultValue(fieldDef)) {
                                        <div class="invalid-feedback">
                                            {{ editor.isValidDefaultValue(fieldDef)![0] }}
                                        </div>
                                    }
                                    <small class="form-text"> Optional default value for form initialization </small>
                                } @else if (
                                    fieldDef.fieldType === "CATEGORIES" ||
                                    fieldDef.fieldType === "REQUIREMENTS" ||
                                    fieldDef.fieldType === "ORGANIZATION_MULTISELECT" ||
                                    fieldDef.fieldType === "CONTACT_ROLE" ||
                                    fieldDef.fieldType === "OBJECT_OCCURRENCE" ||
                                    fieldDef.fieldType === "REGISTRANTS" ||
                                    fieldDef.fieldType === "CUSTOM_ATTRIBUTE" ||
                                    fieldDef.fieldType === "FILE" ||
                                    fieldDef.fieldType === "IMAGE" ||
                                    fieldDef.fieldType === "FORM" ||
                                    fieldDef.fieldType === "EVENT" ||
                                    fieldDef.fieldType === "SECTION" ||
                                    fieldDef.fieldType === "MULTISELECT_OCCURRENCES" ||
                                    fieldDef.fieldType === "ORG_CATEGORIES" ||
                                    fieldDef.fieldType === "ORG_CUSTOM_ATTRIBUTE" ||
                                    fieldDef.fieldType === "ORG_CONTACT_ROLE" ||
                                    fieldDef.fieldType === "LIVE_25_EVENT_FORM" ||
                                    fieldDef.fieldType === "ELECTION_POSITION"
                                ) {
                                    <small class="form-text">
                                        Default values are not supported for this field type.
                                    </small>
                                } @else if (fieldDef.fieldType === "RICH_TEXT") {
                                    <s25-ng-rich-text-editor
                                        [(modelValue)]="fieldDef.defaultValue"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    ></s25-ng-rich-text-editor>
                                    @if (editor.isValidDefaultValue(fieldDef)) {
                                        <div class="invalid-feedback">
                                            {{ editor.isValidDefaultValue(fieldDef)![0] }}
                                        </div>
                                    }
                                    <small class="form-text"> Optional default value for form initialization </small>
                                } @else {
                                    <!-- Text input for other field types -->
                                    <input
                                        class="form-control"
                                        [class.is-invalid]="editor.isValidDefaultValue(fieldDef) !== null"
                                        type="text"
                                        [(ngModel)]="fieldDef.defaultValue"
                                        [placeholder]="editor.getDefaultValuePlaceholder(fieldDef)"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    />
                                    @if (editor.isValidDefaultValue(fieldDef)) {
                                        <div class="invalid-feedback">
                                            {{ editor.isValidDefaultValue(fieldDef)![0] }}
                                        </div>
                                    }
                                    <small class="form-text">
                                        @if (fieldDef.fieldType === "MULTISELECT") {
                                            Use | to separate multiple values
                                        } @else {
                                            Optional default value for form initialization
                                        }
                                    </small>
                                }
                            </div>
                        </div>

                        <div class="form-row checkbox-row">
                            <s25-ng-checkbox [(value)]="fieldDef.hidden" [disabled]="editor.isSystemField(fieldDef)">
                                Hidden Field
                            </s25-ng-checkbox>
                            <s25-ng-checkbox
                                [(value)]="fieldDef.required"
                                [disabled]="editor.isSystemField(fieldDef) || fieldDef.fieldType === 'TOGGLE'"
                            >
                                Required Field
                            </s25-ng-checkbox>
                            <s25-ng-checkbox [(value)]="fieldDef.readOnly" [disabled]="editor.isSystemField(fieldDef)">
                                Read Only
                            </s25-ng-checkbox>
                            @if (editor.ingestionPoint.allowEditAfterSubmit) {
                                <s25-ng-checkbox
                                    [(value)]="fieldDef.requiresReapproval"
                                    [disabled]="editor.isSystemField(fieldDef)"
                                >
                                    Requires Re-approval
                                </s25-ng-checkbox>
                            }
                        </div>

                        @if (fieldDef.fieldType === "OBJECT_OCCURRENCE") {
                            <div class="form-row c-margin-top--single">
                                <div class="form-group">
                                    <label class="ngBlock c-margin-bottom--single">Occurrence field options</label>
                                    <s25-ng-checkbox
                                        [value]="
                                            editor.parseObjectOccurrenceFieldConfig(fieldDef.fieldConfig).showLocations
                                        "
                                        (valueChange)="
                                            editor.setObjectOccurrenceFieldConfigFlag(fieldDef, 'showLocations', $event)
                                        "
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    >
                                        Show locations (search and pick per occurrence)
                                    </s25-ng-checkbox>
                                    <s25-ng-checkbox
                                        [value]="
                                            editor.parseObjectOccurrenceFieldConfig(fieldDef.fieldConfig).showResources
                                        "
                                        (valueChange)="
                                            editor.setObjectOccurrenceFieldConfigFlag(fieldDef, 'showResources', $event)
                                        "
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    >
                                        Show resources (search and pick per occurrence)
                                    </s25-ng-checkbox>
                                </div>
                            </div>
                        }
                        @if (fieldDef.fieldType === "LIVE_25_EVENT_FORM") {
                            <div class="form-row c-margin-top--single">
                                <div class="form-group">
                                    <label class="ngBlock c-margin-bottom--single"
                                        >Force a 25Live form configuration (optional)</label
                                    >
                                    <select
                                        class="form-control"
                                        [ngModel]="
                                            editor.parseLive25EventFormFieldConfig(fieldDef).eventFormConfigurationId
                                        "
                                        (ngModelChange)="editor.setLive25EventFormConfigurationId(fieldDef, $event)"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    >
                                        <option [ngValue]="null">Default (based on user's security group)</option>
                                        @for (cfg of editor.eventFormConfigs.value() ?? []; track cfg.itemId) {
                                            <option [ngValue]="cfg.itemId">
                                                {{ cfg.itemName }}
                                            </option>
                                        }
                                    </select>
                                    <small class="form-text">
                                        When unspecified, the 25Live event form picks the configuration based on the
                                        user's security group.
                                    </small>
                                </div>
                            </div>
                        }
                        @if (fieldDef.fieldType === "ELECTION_POSITION") {
                            <div class="form-row c-margin-top--single">
                                <div class="form-group">
                                    <label class="ngBlock c-margin-bottom--single"
                                        >Position restrictions (optional)</label
                                    >
                                    <small class="form-text c-margin-bottom--single">
                                        Limits which positions nominees can choose per election. If unset for an
                                        election, all positions are shown.
                                    </small>
                                    @if (editor.getElectionPositionRestrictionRows(fieldDef).length) {
                                        <ul class="election-position-config__rows">
                                            @for (
                                                row of editor.getElectionPositionRestrictionRows(fieldDef);
                                                track row.electionId
                                            ) {
                                                <li class="election-position-config__row">
                                                    <div class="election-position-config__election">
                                                        <s25-ng-dropdown-search-criteria
                                                            [type]="'elections'"
                                                            [value]="
                                                                editor.getElectionPositionRestrictionElectionItem(
                                                                    row.electionId
                                                                )
                                                            "
                                                            (valueChange)="
                                                                editor.onElectionPositionRestrictionElectionChange(
                                                                    fieldDef,
                                                                    row.electionId,
                                                                    $event
                                                                )
                                                            "
                                                            [placeholder]="'Select election'"
                                                            [readOnly]="editor.isSystemField(fieldDef)"
                                                        />
                                                    </div>
                                                    <div class="election-position-config__positions">
                                                        <s25-ng-multiselect-search-criteria
                                                            [type]="'electionPositionsForElection'"
                                                            [customFilterValue]="
                                                                editor.getElectionPositionRestrictionFilter(
                                                                    row.electionId
                                                                )
                                                            "
                                                            [selectedItems]="
                                                                editor.getElectionPositionRestrictionSelectedItems(
                                                                    fieldDef,
                                                                    row.electionId
                                                                )
                                                            "
                                                            (changed)="
                                                                editor.onElectionPositionRestrictionPositionsChange(
                                                                    fieldDef,
                                                                    row.electionId,
                                                                    $event
                                                                )
                                                            "
                                                            [showResults]="true"
                                                            [disabled]="editor.isSystemField(fieldDef)"
                                                        />
                                                    </div>
                                                    <s25-ng-button
                                                        [type]="'outline'"
                                                        [onClick]="
                                                            editor.removeElectionPositionRestrictionRowFactory(
                                                                fieldDef,
                                                                row.electionId
                                                            )
                                                        "
                                                        [disabled]="editor.isSystemField(fieldDef)"
                                                        >Remove</s25-ng-button
                                                    >
                                                </li>
                                            }
                                        </ul>
                                    } @else {
                                        <p class="c-text--secondary">No election restrictions configured.</p>
                                    }
                                    <s25-ng-button
                                        class="c-margin-top--single"
                                        [type]="'outline'"
                                        [onClick]="editor.addElectionPositionRestrictionRowFactory(fieldDef)"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                        >Add election</s25-ng-button
                                    >
                                </div>
                            </div>
                        }
                        @if (fieldDef.fieldType === "CATEGORIES" || fieldDef.fieldType === "ORG_CATEGORIES") {
                            @let categoriesConfig = editor.parseCategoriesFieldConfig(fieldDef.fieldConfig);
                            <div class="form-row c-margin-top--single">
                                <div class="form-group">
                                    <label class="ngBlock c-margin-bottom--single">Category field options</label>
                                    <s25-ng-checkbox
                                        [value]="categoriesConfig.singleSelect"
                                        (valueChange)="editor.setCategoriesFieldConfigSingleSelect(fieldDef, $event)"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    >
                                        Single-select
                                    </s25-ng-checkbox>
                                    <s25-ng-checkbox
                                        class="c-margin-top--single"
                                        [value]="categoriesConfig.expandAutomatically"
                                        (valueChange)="
                                            editor.setCategoriesFieldConfigExpandAutomatically(fieldDef, $event)
                                        "
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    >
                                        Expand categories automatically
                                    </s25-ng-checkbox>
                                    @if (!categoriesConfig.expandAutomatically) {
                                        <div class="form-group c-margin-top--single">
                                            <label for="categories-button-label-{{ fieldDef.fieldDefinitionId }}"
                                                >Button label</label
                                            >
                                            <input
                                                id="categories-button-label-{{ fieldDef.fieldDefinitionId }}"
                                                class="c-input"
                                                type="text"
                                                [ngModel]="categoriesConfig.buttonLabel ?? ''"
                                                (ngModelChange)="
                                                    editor.setCategoriesFieldConfigButtonLabel(fieldDef, $event)
                                                "
                                                [disabled]="editor.isSystemField(fieldDef)"
                                                placeholder="{{
                                                    fieldDef.fieldType === 'CATEGORIES'
                                                        ? 'Select Event Categories'
                                                        : 'Select Organization Categories'
                                                }}"
                                            />
                                        </div>
                                    }
                                    <small class="form-text c-margin-bottom--single">
                                        Optionally limit which categories appear on the form. Leave none selected to
                                        allow all categories.
                                    </small>
                                    @if (fieldDef.fieldType === "CATEGORIES") {
                                        <s25-ng-multiselect-search-criteria
                                            [type]="'eventCategories'"
                                            [selectedItems]="editor.getCategoriesAdminFilterSelected(fieldDef)"
                                            (changed)="editor.onCategoriesFieldConfigItemsChange(fieldDef, $event)"
                                            [showResults]="true"
                                            [disabled]="editor.isSystemField(fieldDef)"
                                        />
                                    } @else {
                                        <s25-ng-multiselect-search-criteria
                                            [type]="'organizationCategories'"
                                            [selectedItems]="editor.getCategoriesAdminFilterSelected(fieldDef)"
                                            (changed)="editor.onCategoriesFieldConfigItemsChange(fieldDef, $event)"
                                            [showResults]="true"
                                            [disabled]="editor.isSystemField(fieldDef)"
                                        />
                                    }
                                </div>
                            </div>
                        }
                        @if (fieldDef.fieldType === "SECTION") {
                            @let sectionConfig = editor.parseSectionFieldConfig(fieldDef.fieldConfig);
                            <div class="form-row c-margin-top--single">
                                <div class="form-group">
                                    <label class="ngBlock c-margin-bottom--single">Section appearance</label>
                                    <div class="form-group">
                                        <label>Header image</label>
                                        @if (editor.canUploadSectionHeaderImage(fieldDef)) {
                                            <s25-ng-filestore-image
                                                #sectionHeaderImage
                                                [value]="sectionConfig.headerImageKey ?? ''"
                                                (valueChange)="
                                                    editor.setSectionFieldConfigHeaderImageKey(fieldDef, $event)
                                                "
                                                [objectType]="-1"
                                                [objectId]="-1"
                                                [fileKey]="editor.sectionHeaderImageFileKey(fieldDef)"
                                                [saveButton]="false"
                                            />
                                            <small class="form-text"
                                                >Choose an image and alt text, then click Save form to upload. Preview
                                                shows the header after save.</small
                                            >
                                        } @else {
                                            <p class="form-text">Save the form to upload a section header image.</p>
                                        }
                                    </div>
                                    <div class="form-group c-margin-top--single">
                                        <label for="section-heading-font-size-{{ idx }}">Heading font size</label>
                                        <select
                                            id="section-heading-font-size-{{ idx }}"
                                            class="form-control"
                                            [ngModel]="sectionConfig.headingFontSize"
                                            (ngModelChange)="
                                                editor.setSectionFieldConfigHeadingFontSize(fieldDef, $event)
                                            "
                                            [disabled]="editor.isSystemField(fieldDef)"
                                        >
                                            @for (sizeOption of editor.FORM_FONT_SIZE_OPTIONS; track sizeOption.value) {
                                                <option [ngValue]="sizeOption.value">{{ sizeOption.label }}</option>
                                            }
                                        </select>
                                    </div>
                                    <div class="form-group c-margin-top--single section-heading-color-row">
                                        <label for="section-heading-color-{{ idx }}">Heading color</label>
                                        <div class="section-heading-color-controls">
                                            <input
                                                id="section-heading-color-{{ idx }}"
                                                type="color"
                                                class="section-heading-color-swatch"
                                                [ngModel]="
                                                    editor.S25Util.hexForColorPicker(
                                                        sectionConfig.headingColor,
                                                        editor.DEFAULT_SECTION_HEADING_COLOR
                                                    )
                                                "
                                                (ngModelChange)="
                                                    editor.setSectionFieldConfigHeadingColor(fieldDef, $event)
                                                "
                                                [disabled]="editor.isSystemField(fieldDef)"
                                                title="Pick heading color"
                                                aria-label="Pick section heading color"
                                            />
                                            <input
                                                class="form-control section-heading-color-text"
                                                type="text"
                                                [ngModel]="sectionConfig.headingColor ?? ''"
                                                (ngModelChange)="
                                                    editor.setSectionFieldConfigHeadingColor(fieldDef, $event)
                                                "
                                                [disabled]="editor.isSystemField(fieldDef)"
                                                placeholder="{{ editor.DEFAULT_SECTION_HEADING_COLOR }}"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                    <!-- /config-group-body -->
                </details>

                <!-- Text Field Validation -->
                @if (fieldDef.fieldType === "TEXT") {
                    <details class="config-group-collapsible" open>
                        <summary class="config-group-summary">Validation Rules</summary>
                        <div class="config-group-body">
                            <div class="form-row">
                                <div class="form-group">
                                    <label>Min Length</label>
                                    <input
                                        class="form-control"
                                        type="number"
                                        [(ngModel)]="fieldDef.minLength"
                                        min="0"
                                        (ngModelChange)="editor.onTextMinMaxChange(fieldDef, 'min', $event)"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    />
                                </div>
                                <div class="form-group">
                                    <label>Max Length</label>
                                    <input
                                        class="form-control"
                                        type="number"
                                        [(ngModel)]="fieldDef.maxLength"
                                        min="0"
                                        (ngModelChange)="editor.onTextMinMaxChange(fieldDef, 'max', $event)"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    />
                                </div>
                            </div>
                            <div class="form-row">
                                <div class="form-group">
                                    <label>Validation Pattern</label>
                                    <input
                                        class="form-control"
                                        [class.is-invalid]="!editor.isValidRegex(fieldDef.regexValid)"
                                        type="text"
                                        [(ngModel)]="fieldDef.regexValid"
                                        placeholder="e.g., ^[A-Za-z0-9]+$"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    />
                                    @if (
                                        fieldDef.regexValid &&
                                        fieldDef.regexValid.trim() &&
                                        !editor.isValidRegex(fieldDef.regexValid)
                                    ) {
                                        <div class="invalid-feedback">Invalid regex pattern</div>
                                    }
                                    <small class="form-text">Optional regex pattern for validation</small>
                                </div>
                            </div>
                        </div>
                        <!-- /config-group-body -->
                    </details>
                }

                <!-- Stars Rating and Numeric Scale Validation -->
                @if (fieldDef.fieldType === "STARS_RATING" || fieldDef.fieldType === "NUMERIC_SCALE") {
                    <details class="config-group-collapsible" open>
                        <summary class="config-group-summary">Range Configuration</summary>
                        <div class="config-group-body">
                            <div class="form-row">
                                <div class="form-group">
                                    <label>Minimum Value</label>
                                    <input
                                        class="form-control"
                                        type="number"
                                        [(ngModel)]="fieldDef.minLength"
                                        [min]="editor.starsNumericScaleMin()"
                                        [max]="editor.starsNumericScaleMax()"
                                        (ngModelChange)="editor.onStarsNumericScaleRangeChange(fieldDef, 'min', $event)"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    />
                                    <small class="form-text">Minimum rating/scale value (default: 1)</small>
                                </div>
                                <div class="form-group">
                                    <label>Maximum Value</label>
                                    <input
                                        class="form-control"
                                        type="number"
                                        [(ngModel)]="fieldDef.maxLength"
                                        [min]="editor.starsNumericScaleMin()"
                                        [max]="editor.starsNumericScaleMax()"
                                        (ngModelChange)="editor.onStarsNumericScaleRangeChange(fieldDef, 'max', $event)"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    />
                                    <small class="form-text">Maximum rating/scale value (default: 5)</small>
                                </div>
                            </div>
                        </div>
                        <!-- /config-group-body -->
                    </details>
                }

                <!-- Select Options -->
                @if (editor.isOptionBasedFieldType(fieldDef.fieldType)) {
                    <details class="config-group-collapsible" open>
                        <summary class="config-group-summary">Answer Options</summary>
                        <div class="config-group-body">
                            @if (fieldDef.answerType === AnswerType.BOOLEAN) {
                                <div class="boolean-options-note">
                                    <small class="form-text">
                                        Boolean fields are locked to "Yes" (true) and "No" (false) options. These cannot
                                        be modified.
                                    </small>
                                </div>
                            }
                            <div class="options-list">
                                <div s25-ng-dnd-sortable [items]="fieldDef.options || []">
                                    @for (opt of fieldDef.options; track opt; let optIdx = $index) {
                                        <div
                                            class="option-item"
                                            s25-ng-dnd-sortable-item
                                            [index]="optIdx"
                                            [belongsTo]="fieldDef.options"
                                            [draggable]="false"
                                        >
                                            <span class="option-item__drag-handle" draggable="true">
                                                <s25-ng-icon [type]="'dragHandle'" [ariaLabel]="false" />
                                            </span>
                                            <div class="option-input-wrapper">
                                                <input
                                                    class="form-control"
                                                    [class.is-invalid]="!editor.isValidOptionLength(opt.option)"
                                                    type="text"
                                                    [(ngModel)]="opt.option"
                                                    name="option-{{ idx }}-{{ optIdx }}"
                                                    placeholder="Option text"
                                                    [maxlength]="editor.MAX_OPTION_LENGTH"
                                                    [disabled]="fieldDef.answerType === AnswerType.BOOLEAN"
                                                />
                                                @if (!editor.isValidOptionLength(opt.option)) {
                                                    <div class="invalid-feedback">
                                                        Option cannot exceed
                                                        {{ editor.MAX_OPTION_LENGTH }}
                                                        characters
                                                    </div>
                                                }
                                            </div>
                                            <button
                                                class="aw-button aw-button--danger--outline"
                                                (click)="editor.removeOption(fieldDef, opt)"
                                                title="Remove Option"
                                                [disabled]="fieldDef.answerType === AnswerType.BOOLEAN"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    }
                                </div>
                                <div class="add-option">
                                    <div class="add-option-input-wrapper">
                                        <input
                                            class="form-control"
                                            [class.is-invalid]="!editor.isValidOptionLength(fieldDef.newAnswer?.text)"
                                            type="text"
                                            [(ngModel)]="fieldDef.newAnswer.text"
                                            name="new-option-{{ idx }}"
                                            placeholder="Enter new option"
                                            [maxlength]="editor.MAX_OPTION_LENGTH"
                                            [disabled]="fieldDef.answerType === AnswerType.BOOLEAN"
                                        />
                                        @if (!editor.isValidOptionLength(fieldDef.newAnswer?.text)) {
                                            <div class="invalid-feedback">
                                                Option cannot exceed
                                                {{ editor.MAX_OPTION_LENGTH }}
                                                characters
                                            </div>
                                        }
                                    </div>
                                    <button
                                        class="aw-button aw-button--outline"
                                        (click)="editor.addOption(fieldDef)"
                                        [disabled]="fieldDef.answerType === AnswerType.BOOLEAN"
                                    >
                                        + Add Option
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- /config-group-body -->
                    </details>
                }

                <details class="config-group-collapsible">
                    <summary class="config-group-summary">Advanced</summary>
                    <div class="config-group-body">
                        <div class="form-row">
                            <div class="form-group">
                                <label>Reporting Field Code</label>
                                <div class="reporting-code-input-group">
                                    <select
                                        class="form-control reporting-code-dropdown"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                        (change)="
                                            editor.onReportingFieldCodeSelect(fieldDef, $any($event.target).value)
                                        "
                                        [value]="fieldDef.reportingFieldCode || ''"
                                    >
                                        <option value="">-- Select existing code or type new --</option>
                                        @for (code of editor.reportingFieldCodes; track code) {
                                            <option [value]="code">
                                                {{ code }}
                                            </option>
                                        }
                                    </select>
                                    <input
                                        class="form-control reporting-code-input"
                                        [class.is-invalid]="
                                            !editor.isSystemField(fieldDef) &&
                                            !editor.isValidReportingCode(fieldDef.reportingFieldCode)
                                        "
                                        type="text"
                                        [(ngModel)]="fieldDef.reportingFieldCode"
                                        (ngModelChange)="editor.onReportingCodeChange(fieldDef)"
                                        placeholder="Auto-generated from field label"
                                        maxlength="32"
                                        [disabled]="editor.isSystemField(fieldDef)"
                                    />
                                </div>
                                @if (
                                    !editor.isSystemField(fieldDef) &&
                                    fieldDef.reportingFieldCode &&
                                    !editor.isValidReportingCode(fieldDef.reportingFieldCode)
                                ) {
                                    <div class="invalid-feedback">
                                        Only alphanumeric characters allowed, max 32 characters
                                    </div>
                                }
                                <small class="form-text"
                                    >Used for cross-form reporting and automated field mapping. Select from existing
                                    codes or type a new one.</small
                                >
                            </div>
                        </div>
                    </div>
                </details>
            </div>
            <!-- /field-config -->
        </div>
    `,
    styles: `
        .collapse-icon {
            width: 20px;
            height: 20px;
            transition: transform 0.3s ease;
            flex-shrink: 0;
        }

        .collapsible-section-content {
            padding: 20px;
            background: var(--color--surface-bg);
        }

        .field-card--section {
            border-left: 3px solid var(--cn-blue, #0066cc);
        }

        .field-card--section .field-title h4 {
            color: var(--cn-blue, #0066cc);
        }

        .field-type-readonly {
            min-height: 38px;
            display: flex;
            align-items: center;
            padding: 6px 12px;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background: var(--color--page-bg);
            color: var(--color--text-muted);
            font-weight: 500;
        }

        .field-card {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            margin-bottom: 20px;
            overflow: visible; /* Changed from hidden to allow dropdowns to extend beyond card */
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            transition:
                box-shadow 0.2s ease,
                border-left-color 0.15s ease;
        }

        .field-card:hover,
        .field-card:focus-within {
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            border-left: 3px solid var(--cn-blue);
        }

        .field-card__drag-handle {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: move;
            flex-shrink: 0;
        }

        .field-header {
            background: var(--color--page-bg);
            padding: 16px 20px;
            border-bottom: 1px solid var(--color--border);
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            cursor: pointer;
        }

        .field-header-main {
            flex: 1;
            min-width: 0;
            min-height: 44px;
            display: flex;
            align-items: center;
            cursor: pointer;
        }

        .field-title {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .field-title .collapse-icon {
            width: 16px;
            height: 16px;
            min-width: 44px;
            min-height: 44px;
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin: -12px 0;
        }

        .field-title h4 {
            margin: 0;
            color: var(--color--text-muted);
            font-weight: 600;
            font-size: 16px;
        }

        .field-badges {
            display: flex;
            gap: 6px;
        }

        .badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .badge-primary {
            background-color: #007bff;
            color: white;
        }

        .badge-secondary {
            background-color: #6c757d;
            color: white;
        }

        .badge-warning {
            background-color: #ffc107;
            color: #212529;
        }

        .action-controls {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
        }

        .field-config {
            padding: 20px;
            overflow: visible;
        }

        .config-group-collapsible {
            margin-bottom: 24px;
        }

        .config-group-collapsible:last-child {
            margin-bottom: 0;
        }

        .config-group-summary {
            cursor: pointer;
            padding: 10px 14px;
            font-weight: 600;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: var(--color--text-muted);
            list-style: none;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid var(--color--border);
            user-select: none;
        }

        details:not([open]) .config-group-summary {
            border-bottom: 1px solid var(--color--border);
        }

        .config-group-summary::after {
            content: "▾";
            transition: transform 0.2s;
        }

        details[open] .config-group-summary::after {
            transform: rotate(180deg);
        }

        .config-group-body {
            padding: 16px 14px;
        }

        .form-row {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 16px;
            margin-bottom: 16px;
        }

        .form-row:last-child {
            margin-bottom: 0;
        }

        .form-group {
            display: flex;
            flex-direction: column;
            position: relative; /* Create positioning context for dropdowns to prevent clipping */
        }

        .form-group label {
            font-weight: 500;
            color: var(--color--text-muted);
            margin-bottom: 6px;
            font-size: 13px;
        }

        .form-control {
            padding: 8px 12px;
            border: 1px solid #ced4da;
            border-radius: 4px;
            font-size: 14px;
            transition:
                border-color 0.15s ease-in-out,
                box-shadow 0.15s ease-in-out;
        }

        .form-control:focus {
            border-color: #007bff;
            outline: 0;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .form-control.is-invalid {
            border-color: #dc3545;
        }

        .form-control.is-invalid:focus {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
        }

        .invalid-feedback {
            color: #dc3545;
            font-size: 12px;
            margin-top: 4px;
        }

        .form-text {
            color: var(--color--text-muted);
            font-size: 12px;
            margin-top: 4px;
        }

        .char-count--over {
            color: var(--color--text-danger);
        }

        .reporting-code-input-group {
            display: flex;
            gap: 8px;
            align-items: stretch;
        }

        .reporting-code-dropdown {
            flex: 0 0 200px;
            min-width: 200px;
        }

        .reporting-code-input {
            flex: 1;
        }

        .checkbox-row {
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-top: 8px;
        }

        .options-list {
            border: 1px solid var(--color--border);
            border-radius: 4px;
            padding: 12px;
            background-color: var(--color--page-bg);
        }

        .option-item__drag-handle {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: move;
            flex-shrink: 0;
        }

        .option-item {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            margin-bottom: 8px;
        }

        .option-item:last-child {
            margin-bottom: 0;
        }

        .option-input-wrapper {
            flex: 1;
            display: flex;
            flex-direction: column;
        }

        .add-option {
            display: flex;
            gap: 8px;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid var(--color--border);
        }

        .add-option-input-wrapper {
            flex: 1;
            display: flex;
            flex-direction: column;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
            .form-row {
                grid-template-columns: 1fr;
            }

            .field-header {
                flex-direction: row;
                align-items: center;
                gap: 8px;
                padding: 12px 16px;
            }

            .field-title {
                flex-wrap: wrap;
                gap: 6px;
            }

            .field-title h4 {
                font-size: 14px;
            }

            .action-controls {
                align-self: center;
                flex-shrink: 0;
            }

            .checkbox-row {
                flex-direction: column;
                gap: 12px;
            }

            .add-option {
                flex-direction: column;
            }

            .section-heading-color-controls {
                display: flex;
                align-items: center;
                gap: 0.5rem;
            }

            .section-heading-color-swatch {
                width: 2.5rem;
                height: 2.5rem;
                padding: 0;
                border: 1px solid #ccc;
                border-radius: 4px;
                cursor: pointer;
                flex-shrink: 0;
            }

            .section-heading-color-text {
                max-width: 8rem;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class IngestionAdminFormFieldCardComponent {
    protected readonly AnswerType = AnswerType;

    @Input({ required: true }) editor!: IngestionAdminFormComponent;
    @Input({ required: true }) fieldsTab!: IngestionAdminFormFieldsTabComponent;
    @Input({ required: true }) fieldDef!: IngestionFieldDefinition;
    @Input({ required: true }) idx!: number;

    @ViewChild("sectionHeaderImage")
    private sectionHeaderImage?: S25FilestoreImageComponent;

    /** Uploads a pending section header image (and alt) to FileStore when Save form runs. */
    async flushPendingSectionHeaderImage(): Promise<string | undefined> {
        if (this.fieldDef.fieldType !== "SECTION") {
            return undefined;
        }
        const filestoreImage = this.sectionHeaderImage;
        if (!filestoreImage) {
            return undefined;
        }
        await filestoreImage.guardedSave();
        return filestoreImage.errorMsg()?.trim() || undefined;
    }

    protected isInfoTextOverLimit(infoText: string | undefined): boolean {
        return isOverCharLimit(infoText, INGESTION_INFO_TEXT_MAX_LENGTH);
    }

    protected formatInfoTextRemainingMessage(infoText: string | undefined): string {
        return formatCharCountRemaining(infoText, INGESTION_INFO_TEXT_MAX_LENGTH, true);
    }
}
